/**
 * SSGlobalSearch — top-bar grouped typeahead.
 *
 * Calls /api/parties-module/v1/search with a 200 ms debounce; renders results
 * grouped by entity kind (Persons / Organizations / Identifiers / Contacts).
 *
 * Props:
 *   onPick(party_id, item)   — required.  Fired when the user clicks a row or
 *                              presses Enter on the active row.
 *   placeholder              — optional.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ssPartyAPI } from '../../../services/ssApi';
import { COLOR, RADIUS, FONT } from '../../../components/ss/tokens';
import { Spinner } from '../../../components/ss';

const GROUPS = [
  { key: 'persons',       label: 'People',         icon: '👤' },
  { key: 'organizations', label: 'Organizations',  icon: '🏢' },
  { key: 'identifiers',   label: 'Identifiers',    icon: '🪪' },
  { key: 'contacts',      label: 'Contacts',       icon: '✉️' },
];

export default function SSGlobalSearch({ onPick, placeholder = 'Search parties, identifiers, contacts…', autoFocus = false }) {
  const [q, setQ]             = useState('');
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState({});
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef(null);
  // Sprint-3 audit item #18 — give the global search a ref so the ⌘K /
  // Ctrl+K shortcut below can focus + select the existing query without
  // mounting a second listener on every page.
  const inputRef = useRef(null);

  // ⌘K (mac) / Ctrl+K (win/linux) — focus and select the search input.
  // Skips if the user is already in a textarea / contenteditable so we
  // never steal a keystroke from their typing.
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      if (tag === 'TEXTAREA') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onGlobalKey = (e) => {
      const isShortcut = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (!isShortcut) return;
      // Allow native browser ⌘K when the user is mid-edit in a textarea.
      if (isTypingTarget(e.target) && e.target !== inputRef.current) return;
      e.preventDefault();
      const node = inputRef.current;
      if (!node) return;
      node.focus();
      try { node.select(); } catch (_) { /* JSDOM safety */ }
      setOpen(true);
    };
    document.addEventListener('keydown', onGlobalKey);
    return () => document.removeEventListener('keydown', onGlobalKey);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!q || q.trim().length < 1) { setData({}); return; }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await ssPartyAPI.globalSearch(q.trim(), 8);
        setData(res?.data || {});
        setActiveIdx(0);
      } catch (e) { setData({}); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(handle);
  }, [q]);

  // Click-outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Flatten results in display order for keyboard nav
  const flat = [];
  GROUPS.forEach(g => {
    (data[g.key] || []).forEach(item => flat.push({ group: g, item }));
  });

  const pick = (entry) => {
    const id = entry.item.partyid;
    onPick && onPick(id, entry);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open && e.key === 'ArrowDown') { setOpen(true); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter')     { e.preventDefault(); pick(flat[activeIdx]); }
    else if (e.key === 'Escape')    { setOpen(false); }
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', maxWidth: 640 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fff',
        border: `1px solid ${COLOR.grayBorder}`,
        borderRadius: RADIUS.pill,
        padding: '6px 14px',
        boxShadow: open ? `0 0 0 3px ${COLOR.blueBg}` : 'none',
        transition: 'box-shadow .15s',
      }}>
        <span style={{ fontSize: 14, color: COLOR.textMuted }}>🔍</span>
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          style={{
            flex: 1, border: 'none', outline: 'none',
            fontSize: FONT.size?.md || 14,
            color: COLOR.text, background: 'transparent',
          }}
        />
        {loading && <Spinner size="sm" label="" />}
        {q && !loading && (
          <button
            onClick={() => { setQ(''); setData({}); }}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: COLOR.textMuted, fontSize: 14,
            }}
            aria-label="Clear search"
          >✕</button>
        )}
      </div>

      {/* Dropdown */}
      {open && q && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#fff',
          border: `1px solid ${COLOR.grayBorder}`,
          borderRadius: RADIUS.md,
          boxShadow: '0 8px 24px rgba(13,27,46,0.12)',
          maxHeight: 480, overflowY: 'auto',
          zIndex: 30,
        }}>
          {!flat.length && !loading && (
            <div style={{ padding: 20, textAlign: 'center', color: COLOR.textMuted, fontSize: 13 }}>
              No matches for "{q}"
            </div>
          )}

          {GROUPS.map(g => {
            const items = data[g.key] || [];
            if (!items.length) return null;
            return (
              <div key={g.key}>
                <div style={{
                  padding: '6px 12px', fontSize: 11, fontWeight: 600,
                  color: COLOR.textMuted, textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  background: COLOR.grayBg,
                  borderBottom: `1px solid ${COLOR.grayBorder}`,
                }}>
                  {g.icon} {g.label}
                </div>
                {items.map((item, j) => {
                  const flatIdx = flat.findIndex(f => f.group.key === g.key && f.item === item);
                  const isActive = flatIdx === activeIdx;
                  return (
                    <button
                      key={`${g.key}-${j}`}
                      onMouseEnter={() => setActiveIdx(flatIdx)}
                      onClick={() => pick({ group: g, item })}
                      style={{
                        display: 'flex', justifyContent: 'space-between',
                        width: '100%', padding: '8px 12px',
                        border: 'none', textAlign: 'left',
                        background: isActive ? COLOR.blueBg : '#fff',
                        cursor: 'pointer',
                        borderBottom: `1px solid ${COLOR.graySubtle}`,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: COLOR.text }}>
                          {item.label || item.value || item.partyid}
                        </div>
                        {item.type_code && (
                          <div style={{ fontSize: 11, color: COLOR.textMuted }}>
                            {item.type_code}: {item.value}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: COLOR.textMuted }}>
                        Party #{item.partyid}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
